import type {
  DataGridCheckRowsFn,
  DataGridColumnProps,
} from '../../components/DataGrid/DataGrid';
import type { DataGridGetRowLinkOptionsFn } from '../../components/DataGrid/DroppableCell/DroppableCell';
import type { DataGridHeaderFilter } from '../../components/DataGrid/HeaderFilter/HeaderFilter';
import type { DataGridColumnOptionsProps } from '../../components/DataGridControl/DataGridControl';
import type {
  ContentObject,
  ContentTypeDefinition,
} from '../../lib/flotiq-client/types/api-response';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import {
  batchDeleteContentObjects,
  getSearchObject,
  listContentTypes,
} from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { GridAddElementEvent } from '../../lib/flotiq-plugins/plugin-events/GridAddElementEvent';
import { GridRenderEvent } from '../../lib/flotiq-plugins/plugin-events/GridRenderEvent';
import {
  getObjectTitle,
  getTestProps,
  setInitialGridOptions,
  updateLocalStorageGridOptions,
  validateErrorMessage,
} from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import CustomizableDataGrid from '../../components/CustomizableDataGrid/CustomizableDataGrid';
import DataGridCell, {
  INPUT_TYPES_TO_RENDER,
} from '../../components/DataGrid/DataGridCell/DataGridCell';
import { useGridFilters } from '../../components/DataGrid/useGridFilters';
import DataGridControl from '../../components/DataGridControl/DataGridControl';
import ElementFromPlugin from '../../components/ElementFromPlugin/ElementFromPlugin';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';
import StatusBar from '../../components/StatusBar/StatusBar';
import Tooltip from '../../components/Tooltip/Tooltip';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';
import TopbarSynchronizationButton from '../../components/Topbar/buttons/TopbarExportButton';

import { useContentObjects, useContentType } from '../../hooks/api';
import { useAllSpaceConstraints } from '../../hooks/api/useAllSpaceConstraints';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useSpaceSynchronization from '../../hooks/synchronization/useSpaceSynchronization';
import { useDuplicateModal } from '../../hooks/useDuplicateModal/useDuplicateModal';
import useObjectStatusChange from '../../hooks/useObjectStatusChange/useObjectStatusChange';
import usePluginResults from '../../hooks/usePluginResults';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import {
  ExportIcon,
  ImportIcon,
  WarningIcon,
  ZoomMaleWorkingImage,
} from '../../images/shapes';

import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import MassEditModalForm from './MassEditModalForm/MassEditModalForm';

const DISPLAY_STATE = {
  LOADING_CTD: 0,
  EMPTY_CTD: 1,
  LOADING_DATA: 2,
  DATA: 3,
  EMPTY_FILTER: 4,
};

const testId = 'content-type-objects';

interface ContentTypeObjectsProps {
  /**
   * Information if we want to allow internals
   */
  allowInternals?: boolean;
  /**
   * Defined columns when we want to specify them
   */
  definedColumns?: any[] | string;
  /**
   * Defined content type name
   */
  definedTypeName?: string;
}

const ContentTypeObjects = ({
  allowInternals = false,
  definedTypeName = '',
  definedColumns = '',
}: ContentTypeObjectsProps) => {
  const { t, i18n } = useTranslation();
  const modal = useModals();
  const { permissions } = use(UserContext);
  const [searchParams] = useSearchParams();

  let { contentTypeName } = useParams();
  if (definedTypeName) {
    contentTypeName = definedTypeName;
  }

  const { canCreate, canRead } = useMemo(
    () => permissions.getCoPermissions(contentTypeName),
    [contentTypeName, permissions],
  );

  const canCreateCtd = useMemo(
    () =>
      permissions.canCtd(
        contentTypeName,
        RolePermissions.PERMISSIONS_TYPES.CREATE,
      ),
    [contentTypeName, permissions],
  );

  const { allConstraints, reload: reloadConstraints } =
    useAllSpaceConstraints();

  const { space, buildUrlWithSpace, spaceLimits } = useSelectedSpace();
  const { handleExport, handleImport } = useSpaceSynchronization(space);

  const sortingLocalStorageKey = `cms._sort_content-type-objects-${contentTypeName}`;
  const gridLocalStorageKey = `cms.content-type-objects-grid-state-v3-${contentTypeName}`;
  const dataGridContainer = useRef<HTMLDivElement>(null);

  const [firstLoading, setFirstLoading] = useState(true);
  const [columns, setColumns] = useState<DataGridColumnProps[]>([]);
  const [isDeleting, setDeleting] = useState(false);
  const [checkedRows, setCheckedRows] = useState<string[]>([]);

  const jwt = useToken();
  const [editGrid, setEditGrid] = useState(false);
  const [filterGrid, setFilterGrid] = useState(!!searchParams.get('filters'));
  const [initDataHasContent, setInitDataHasContent] = useState(false);
  const isFavorites = searchParams.has('favorites');

  const [sort, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(
      sortingLocalStorageKey,
    );
  const [gridOptions, setGridOptions, { removeItem: removeGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(gridLocalStorageKey);

  const { isPending: isTransiting, updateStatuses } =
    useObjectStatusChange(true);

  const {
    filters,
    filtersApplied: hasFilters,
    handleFiltersChange,
    page,
    handlePageChange,
    limit,
    handleLimitChange,
    removeAllFilters,
  } = useGridFilters(`objects-${contentTypeName}`, true);

  const clearPage = useCallback(() => {
    setCheckedRows([]);
    setEditGrid(false);
    setInitDataHasContent(false);
  }, []);

  const objectParams = useMemo(() => {
    const urlFilters = searchParams.get('filters');

    return {
      page,
      limit,

      ...(sort?.sortOrder ? { order_direction: sort?.sortOrder } : {}),
      ...(sort?.sortField ? { order_by: sort?.sortField } : {}),

      ...(urlFilters ? { filters: urlFilters } : {}),
    };
  }, [searchParams, page, limit, sort?.sortOrder, sort?.sortField]);

  const {
    entity: contentType,
    errors: contentTypeErrors,
    status: contentTypeStatus,
  } = useContentType(contentTypeName);

  const { data, errors, isLoading, pagination, reload, isFetching } =
    useContentObjects(contentTypeName, objectParams);

  const [objectToDuplicate, setObjectToDuplicate] = useState<
    ContentObject | undefined
  >();

  const { onDuplicate } = useDuplicateModal(contentType, testId);

  const notAllowed = useMemo(() => {
    if (definedTypeName) {
      return !allowInternals || !canRead;
    }
    return (contentType?.internal && contentTypeName !== '_tag') || !canRead;
  }, [
    allowInternals,
    canRead,
    contentType?.internal,
    contentTypeName,
    definedTypeName,
  ]);

  const label = useMemo(() => contentType?.label || '', [contentType]);

  useApiErrorsToast(errors);
  useApiErrorsToast(contentTypeErrors);

  useEffect(() => {
    if (pagination?.total_pages < page)
      handlePageChange(pagination.total_pages);
  }, [handlePageChange, page, pagination?.total_pages]);

  const handleInitialGridOptions = useCallback(
    (
      cols: DataGridColumnProps[],
      minWidth: number,
      contentType: ContentTypeDefinition | undefined,
    ) => {
      setInitialGridOptions(
        cols,
        minWidth,
        (dataGridContainer.current?.offsetWidth || 0) - 50,
        setGridOptions,
        setColumns,
        contentType,
      );
    },
    [setGridOptions],
  );

  const limitReachedLabel = useMemo(() => {
    let label =
      spaceLimits?.cto_limit !== -1 &&
      (allConstraints?.['content-objects-count'] || 0) >= spaceLimits?.cto_limit
        ? t('ObjectsOfType.LimitExceeded')
        : '';

    if (definedTypeName === '_webhooks') {
      label =
        spaceLimits?.webhooks_limit !== -1 &&
        (allConstraints?.['webhooks-count'] || 0) >= spaceLimits?.webhooks_limit
          ? t('Webhooks.LimitExceeded')
          : label;
    }

    return label;
  }, [
    allConstraints,
    definedTypeName,
    spaceLimits?.cto_limit,
    spaceLimits?.webhooks_limit,
    t,
  ]);

  useEffect(() => {
    clearPage();
  }, [clearPage]);

  const handleFetchDatasource = useCallback(
    async (
      query?: string,
      option?: {
        relationContenttype?: string;
        relationMultiple?: boolean;
      },
    ) => {
      try {
        const {
          body: objects,
          status: objectsStatus,
          ok: objectsOk,
        } = await getSearchObject(jwt, space, {
          q: query ? `*${query}*` : '*',
          limit: 10,
          order_by: 'internal.updatedAt',
          order_direction: 'asc',
          page: 1,
          'fields[]': 'object_data',
          ...(option &&
          'relationContenttype' in option &&
          option.relationContenttype
            ? {
                'content_type[]': option.relationContenttype,
              }
            : {}),
        });

        if (!objectsOk) {
          throw parseResponseErrors(objects, objectsStatus);
        }
        const ctdsName = [
          ...new Set(objects.data.map((el) => el.item.internal.contentType)),
        ];

        const ctdDict: Record<string, ContentTypeDefinition> = {};

        const {
          body: ctds,
          status,
          ok,
        } = await listContentTypes(jwt, space, {
          names: ctdsName,
          limit: ctdsName.length,
        });
        if (!ok) {
          throw parseResponseErrors(ctds, status);
        }

        for (const ctd of ctds.data) {
          ctdDict[ctd.name] = ctd;
        }

        return objects.data.map((el) => ({
          value: el?.item?.internal?.contentType
            ? `/api/v1/content/${el.item.internal.contentType}/${el.item.id}`
            : el.item.id,
          label: getObjectTitle(el.item, ctdDict[el.item.internal.contentType]),
        }));
      } catch (error) {
        toast.error(
          error instanceof ResponseError
            ? error.message
            : t('Form.CommunicationErrorMessage'),
        );
      }
      return [];
    },
    [jwt, space, t],
  );

  useEffect(() => {
    // Remove toasts on change content type
    toast.dismiss();

    // Remove toasts on exit
    return () => {
      toast.dismiss();
    };
  }, [contentType, contentTypeName]);

  const handleDeleteObjects = useCallback(
    async (checkedRows: string[]) => {
      setDeleting(true);

      // Remove previously added infinity toasts on errors
      toast.dismiss();

      if (checkedRows.length > 10) {
        modal.info(
          t('Global.Deleting'),
          t('Global.DeletingMany'),
          'delete-modal',
        );
      } else {
        modal.deleting('delete-modal');
      }

      try {
        const { body, status, ok } = await batchDeleteContentObjects(
          jwt,
          space,
          Object.assign(checkedRows, {
            contentTypeName,
          }),
        );

        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(
          t('ObjectsOfType.DeletedCount', {
            deletedCount: body.deletedCount,
          }),
        );

        setCheckedRows([]);
        reload?.();
        reloadConstraints(['content-objects-count']);
        setDeleting(false);
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(
            error.message
              ? validateErrorMessage(error.message)
              : t('ContentForm.CouldntDelete'),
            {
              duration: Infinity,
            },
          );
        }
        setCheckedRows([]);
        reload?.();
        setDeleting(false);
      }
    },
    [modal, t, jwt, space, contentTypeName, reload, reloadConstraints],
  );

  const showDeleteModal = useCallback(
    (ids: string[]) => {
      let currentConfirmDeleteText = t('ObjectsOfType.ConfirmDelete', {
        count: ids.length,
      });

      if (definedTypeName === '_webhooks') {
        currentConfirmDeleteText = t('ObjectsOfType.ConfirmDeleteWebhooks', {
          count: ids.length,
        });
      }

      if (contentTypeName === '_tag') {
        currentConfirmDeleteText = t('ObjectsOfType.ConfirmDeleteTags', {
          count: ids.length,
        });
      }

      modal.delete(currentConfirmDeleteText, 'delete-modal', () =>
        handleDeleteObjects(ids),
      );
    },
    [t, definedTypeName, contentTypeName, modal, handleDeleteObjects],
  );

  const showDeleteManyModal = useCallback(() => {
    showDeleteModal(checkedRows);
  }, [checkedRows, showDeleteModal]);

  const showMassEditModal = useCallback(async () => {
    await modal({
      title: (
        <div className="text-xl md:text-3xl">
          {t('ObjectsOfType.MassEditTitle', { count: checkedRows.length })}
        </div>
      ),
      content: (
        <MassEditModalForm
          contentTypeName={contentTypeName}
          ids={checkedRows}
          {...getTestProps(testId, 'edit', 'testId')}
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'max-h-[calc(100vh-32px)]',
    });
    setCheckedRows([]);
    reload?.();
  }, [checkedRows, contentTypeName, modal, reload, t]);

  const handeMassPublish = useCallback(() => {
    updateStatuses({
      objects: { [contentTypeName!]: checkedRows },
      action: 'publish',
    });
    setCheckedRows([]);
  }, [checkedRows, updateStatuses, contentTypeName]);

  const handeMassUnpublish = useCallback(() => {
    updateStatuses({
      objects: { [contentTypeName!]: checkedRows },
      action: 'unpublish',
    });
    setCheckedRows([]);
  }, [checkedRows, updateStatuses, contentTypeName]);

  const handeMassArchive = useCallback(() => {
    updateStatuses({
      objects: { [contentTypeName!]: checkedRows },
      action: 'archive',
    });
    setCheckedRows([]);
  }, [checkedRows, updateStatuses, contentTypeName]);

  /**
   * @emits FlotiqPlugins."flotiq.grid::render"
   */
  const gridRenderResult = usePluginResults(
    'flotiq.grid::render',
    GridRenderEvent,
    {
      contentTypeName,
      contentType,
      contentObjects: data,
      pagination,
      handlePageChange,
      isFetching,
      isLoading,
    },
  );

  useEffect(() => {
    if (!contentType) return;
    if (contentTypeName !== contentType.name) return;

    const cols: DataGridColumnProps[] = [];

    const dataColumns = contentType.metaDefinition?.propertiesConfig || {};
    const gridOptionsByID = gridOptions?.reduce(
      (acc, elem, idx) => {
        acc[elem.colId] = { ...elem, idx };
        return acc;
      },
      {} as { [key: string]: DataGridColumnOptionsProps & { idx: number } },
    );

    // Case: generate state of hide based on currentLocalGridOption
    const hiddenColumnsById: Record<string, boolean> = {};
    const orderFromLocalStorage = [];

    for (const key in gridOptions) {
      hiddenColumnsById[gridOptions[+key].colId] =
        gridOptions[+key].hide || false;

      orderFromLocalStorage.push(gridOptions[+key].colId);
    }

    let order = definedColumns || contentType.metaDefinition?.order || [];
    order = [
      ...new Set([
        ...(orderFromLocalStorage || []),
        'id',
        ...order,
        ...(contentType.draftPublic
          ? ['internal.status', 'internal.publishedAt']
          : []),
        ...(typeof contentType.schemaDefinition?.allOf !== 'undefined' &&
        contentType.schemaDefinition.allOf[0]['$ref'] ===
          '#/components/schemas/AbstractContentTypeSchemaDefinition'
          ? ['internal.createdAt', 'internal.updatedAt']
          : []),
      ]),
    ];

    if (orderFromLocalStorage.length) {
      updateLocalStorageGridOptions(order, gridOptions, setGridOptions);
    }

    const idColumnOption = {
      accessor: 'id',
      label: 'ID',
      filterInputType: 'text',
      sortable: !editGrid,
      resizable: true,
      removable: true,
      showByDefault: true,
      width: gridOptionsByID?.['id']?.width,
      hide: hiddenColumnsById['id'],
      render: (data: string, rowData: ContentObject) => (
        <DataGridCell
          inputType="text"
          data={data}
          contentTypeName={contentTypeName}
          accessor="id"
          contentObject={rowData}
          contentType={contentType}
        />
      ),
    };

    const states =
      ['draft', 'modified', 'public', 'archived'].map((status) => {
        return {
          value: status,
          label: t(`ObjectStatus.Status.${status}`),
        };
      }) || [];

    const internalStatusColumn = {
      accessor: 'internal.status',
      label: 'Status',
      filterInputType: 'select',
      filterInputOptions: states,
      sortable: !editGrid,
      resizable: true,
      removable: true,
      showByDefault: true,
      width: gridOptionsByID?.['internal.status']?.width,
      hide: hiddenColumnsById['internal.status'],
      render: (data: string, rowData: ContentObject) => (
        <DataGridCell
          inputType="select"
          data={t(`ObjectStatus.Status.${data}`)}
          color={data}
          contentTypeName={contentTypeName}
          accessor="internal.status"
          contentObject={rowData}
          contentType={contentType}
        />
      ),
    };

    const internalPublishedAtColumn = {
      accessor: 'internal.publishedAt',
      label: t('ObjectStatus.PublicationDate'),
      filterInputType: 'select',
      filterInputOptions: [
        {
          value: '-',
          label: t('Global.True'),
        },
      ],
      sortable: !editGrid,
      resizable: true,
      removable: true,
      width: gridOptionsByID?.['internal.publishedAt']?.width,
      hide: hiddenColumnsById['internal.publishedAt'],
      render: (data: string, rowData: ContentObject) => (
        <DataGridCell
          inputType="datePublished"
          data={data}
          contentTypeName={contentTypeName}
          accessor="internal.publishedAt"
          contentObject={rowData}
          contentType={contentType}
        />
      ),
    };

    const createdAtColumn = {
      accessor: 'internal.createdAt',
      label: t('ObjectsOfType.Created'),
      filterInputType: 'text',
      sortable: !editGrid,
      resizable: true,
      removable: true,
      width: gridOptionsByID?.['internal.createdAt']?.width,
      hide: hiddenColumnsById['internal.createdAt'],
      render: (data: string, rowData: ContentObject) => (
        <DataGridCell
          inputType="text"
          data={data}
          contentTypeName={contentTypeName}
          accessor="internal.createdAt"
          contentObject={rowData}
          contentType={contentType}
        />
      ),
    };

    const updatedAtColumn = {
      accessor: 'internal.updatedAt',
      label: t('ObjectsOfType.Modified'),
      filterInputType: 'text',
      sortable: !editGrid,
      resizable: true,
      removable: true,
      width: gridOptionsByID?.['internal.updatedAt']?.width,
      hide: hiddenColumnsById['internal.updatedAt'],
      render: (data: string, rowData: ContentObject) => (
        <DataGridCell
          inputType="text"
          data={data}
          contentTypeName={contentTypeName}
          accessor="internal.updatedAt"
          contentObject={rowData}
          contentType={contentType}
        />
      ),
    };

    order.forEach((accessor) => {
      if (accessor === 'id') {
        cols.push(idColumnOption);
      } else if (accessor === 'internal.status')
        cols.push(internalStatusColumn);
      else if (accessor === 'internal.publishedAt')
        cols.push(internalPublishedAtColumn);
      else if (accessor === 'internal.createdAt') cols.push(createdAtColumn);
      else if (accessor === 'internal.updatedAt') cols.push(updatedAtColumn);
      else if (
        INPUT_TYPES_TO_RENDER.includes(dataColumns[accessor]?.inputType) &&
        !dataColumns[accessor]?.isPassword &&
        permissions.canCo(
          contentTypeName,
          RolePermissions.PERMISSIONS_TYPES.READ,
          accessor,
        )
      ) {
        const label = dataColumns[accessor]?.label || accessor;
        let currentFilterInputType = dataColumns[accessor]?.inputType;

        if (definedTypeName === '_webhooks' && accessor === 'actions') {
          currentFilterInputType = 'webhookActions';
        }

        cols.push({
          accessor: accessor,
          label,
          filterInputType: currentFilterInputType,
          filterInputOptions:
            dataColumns[accessor]?.options ||
            dataColumns[accessor]?.optionsWithLabels,
          filterValidation: dataColumns[accessor]?.validation,
          filterDatasourceFetch: (query) =>
            handleFetchDatasource(query, dataColumns[accessor]?.validation),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          width: gridOptionsByID?.[accessor]?.width,
          hide: hiddenColumnsById[accessor],
          render: (data, rowData) => {
            let columnData = data;
            if (
              currentFilterInputType === 'select' &&
              dataColumns[accessor].useOptionsWithLabels
            ) {
              columnData =
                dataColumns[accessor].optionsWithLabels?.find(
                  (option) => option.value === data,
                )?.label || data;
            }
            return (
              <DataGridCell
                inputType={currentFilterInputType}
                data={columnData}
                items={dataColumns[accessor]?.items}
                contentTypeName={contentTypeName}
                accessor={accessor}
                contentObject={rowData}
                contentType={contentType}
                testId={testId}
              />
            );
          },
        });
      }
    });
    if (gridRenderResult.length !== 0) return;
    if (!gridOptions) {
      handleInitialGridOptions(cols, 100, contentType);
    } else {
      setColumns(cols);
    }
  }, [
    contentType,
    handleFetchDatasource,
    contentTypeName,
    editGrid,
    t,
    handleInitialGridOptions,
    gridOptions,
    gridLocalStorageKey,
    definedColumns,
    showDeleteManyModal,
    definedTypeName,
    permissions,
    gridRenderResult.length,
    i18n,
    setGridOptions,
  ]);

  const handleResetGrid = useCallback(() => {
    setSort(undefined);
    setCheckedRows([]);
    removeSort();
    removeAllFilters();
    removeGridOptions();
    setGridOptions(undefined);
  }, [
    removeAllFilters,
    removeGridOptions,
    removeSort,
    setGridOptions,
    setSort,
  ]);

  useEffect(() => {
    setFirstLoading(true);
  }, [contentTypeName]);

  useEffect(() => {
    if (firstLoading) {
      setInitDataHasContent(false);
    }
    if (!isLoading) setFirstLoading(false);
  }, [isLoading, firstLoading]);

  const handleCheckRows = useCallback<DataGridCheckRowsFn>(
    (checked) => {
      setCheckedRows(checked as string[]);

      /**
       * We need to save the object data for duplication in case the user selects the object,
       * then changes the page and clicks duplicate.
       * In that case, the current API data will not contain the selected object.
       */
      if (checked.length === 1) {
        setObjectToDuplicate(data.find((obj) => obj.id === checked[0]));
      } else {
        setObjectToDuplicate(undefined);
      }
    },
    [data],
  );

  useEffect(() => {
    if (data.length && !initDataHasContent) {
      setInitDataHasContent(true);
    } else if (data.length === 0 && initDataHasContent) {
      setInitDataHasContent(false);
    }
  }, [data.length, initDataHasContent]);

  const displayLevel = useMemo(() => {
    if (firstLoading) return DISPLAY_STATE.LOADING_CTD;
    if (
      (!data.length && !hasFilters && !initDataHasContent) ||
      contentTypeStatus === 404 ||
      notAllowed
    ) {
      return DISPLAY_STATE.EMPTY_CTD;
    }
    if (isLoading || isDeleting || isTransiting)
      return DISPLAY_STATE.LOADING_DATA;
    if (!data.length && hasFilters) return DISPLAY_STATE.EMPTY_FILTER;
    return DISPLAY_STATE.DATA;
  }, [
    firstLoading,
    data.length,
    hasFilters,
    initDataHasContent,
    contentTypeStatus,
    notAllowed,
    isLoading,
    isDeleting,
    isTransiting,
  ]);

  const parseFilters = useCallback(
    (currentFilters: DataGridHeaderFilter) => {
      const res: DataGridHeaderFilter = {};

      for (const key in currentFilters) {
        const { type, filter } = currentFilters[key];

        let currentFilterType = type;

        if (!currentFilterType) {
          currentFilterType =
            definedTypeName === '_webhooks' && key === 'type'
              ? 'equals'
              : 'contains';
        }

        res[key] = { type: currentFilterType, filter };
      }

      return res;
    },
    [definedTypeName],
  );

  const onFilter = useCallback(
    (currentFilters: DataGridHeaderFilter) => {
      handleFiltersChange(currentFilters, parseFilters);
    },
    [handleFiltersChange, parseFilters],
  );

  const emptyData = useMemo(() => {
    if (contentTypeStatus === 404 || notAllowed)
      return (
        <Heading
          level={2}
          additionalClasses={twMerge(
            'text-3xl md:text-4xl leading-8 dark:text-white',
          )}
        >
          <div
            className="flex flex-col items-center justify-center text-center"
            {...getTestProps(testId, 'not-existing')}
          >
            <WarningIcon className="text-red w-14 md:w-20 mb-3" />
            {t('ObjectsOfType.NotExisting', { contentTypeName })}
          </div>
        </Heading>
      );
    if (displayLevel === DISPLAY_STATE.LOADING_CTD)
      return (
        <Loader
          size={'small'}
          type={'spinner-grid'}
          testId={testId ? `${testId}-loading` : ''}
        />
      );

    let buttonAddPath = buildUrlWithSpace(
      `content-type-objects/add/${contentTypeName}`,
    );
    let buttonAddLabel = t('ObjectsOfType.Add');

    if (definedTypeName === '_webhooks') {
      buttonAddPath = buildUrlWithSpace('webhooks/add');
      buttonAddLabel = t('Global.AddNew', { name: 'Webhook' });
    }

    if (contentTypeName === '_tag') {
      buttonAddLabel = t('Global.AddNew', { name: 'Tag' });
    }

    return (
      <>
        <ZoomMaleWorkingImage className="w-52" />
        <div
          className="text-center font-bold p-5"
          {...getTestProps(testId, 'empty-data')}
        >
          <p className="text-blue-600 text-2xl sm:text-3xl md:text-4xl">
            {t('ContentDefinition.CreateFirst')}
          </p>
          <p className="text-slate-900 dark:text-white 0 text-3xl sm:text-4xl md:text-5xl">
            {t('ObjectsOfType.ObjectOfType', {
              contentTypeName: label,
            })}
          </p>
        </div>
        <Tooltip
          tooltip={
            canCreate ? limitReachedLabel : t('UserRoles.CannotPerformAction')
          }
        >
          <LinkButton
            additionalClasses="mt-1 md:mt-2"
            link={buttonAddPath}
            disabled={!canCreate || !!limitReachedLabel}
            {...getTestProps(testId, 'add', 'testId')}
          >
            {buttonAddLabel}
          </LinkButton>
        </Tooltip>
      </>
    );
  }, [
    contentTypeStatus,
    notAllowed,
    t,
    contentTypeName,
    displayLevel,
    buildUrlWithSpace,
    definedTypeName,
    label,
    limitReachedLabel,
    canCreate,
  ]);

  /**
   * @emits FlotiqPlugins."flotiq.grid::add"
   */
  const beforeGridResults = usePluginResults(
    'flotiq.grid::add',
    GridAddElementEvent,
    {
      contentTypeName,
      contentType,
      contentObjects: data,
      pagination,
      handlePageChange,
      reload,
      isFetching,
      isLoading,
    },
  );

  const hasBeforeGridPlugin =
    displayLevel >= DISPLAY_STATE.EMPTY_CTD && beforeGridResults?.length > 0;

  const appContextData = useMemo(() => {
    if (definedTypeName === '_webhooks') {
      return {
        page: 'webhooks',
        menuItemOpen: '',
      };
    }
    if (contentTypeName === '_tag') {
      return {
        page: 'tags',
        menuItemOpen: '',
      };
    }

    if (!label) return {};
    return {
      page: isFavorites
        ? `favorites/${label || contentTypeName}-pinned`
        : `content/${label || contentTypeName}`,
      menuItemOpen: isFavorites ? 'favorites' : 'content',
    };
  }, [contentTypeName, definedTypeName, isFavorites, label]);

  const { title, addNew, addNewLink } = useMemo(() => {
    if (definedTypeName === '_webhooks') {
      return {
        title: t('Global.Webhooks'),
        addNew: t('Global.AddNew', { name: 'Webhook' }),
        addNewLink: buildUrlWithSpace('webhooks/add'),
      };
    }
    if (contentTypeName === '_tag') {
      return {
        title: t('Global.Tags'),
        addNew: t('Global.AddNew', { name: 'Tag' }),
        addNewLink: buildUrlWithSpace(
          `content-type-objects/add/${contentTypeName}`,
        ),
      };
    }
    return {
      title: t('ObjectsOfType.Title', { contentTypeName: label }),
      addNew: t('ContentForm.Add', { contentTypeName: label }),
      addNewLink: buildUrlWithSpace(
        `content-type-objects/add/${contentTypeName}`,
      ),
    };
  }, [buildUrlWithSpace, contentTypeName, definedTypeName, label, t]);

  const getRowLinkOptions = useCallback<DataGridGetRowLinkOptionsFn>(
    (_, rowData) =>
      contentTypeName === '_webhooks'
        ? { to: buildUrlWithSpace(`webhooks/edit/${rowData.id}`) }
        : {
            to: buildUrlWithSpace(
              `content-type-objects/edit/${contentTypeName}/${rowData.id}`,
            ),
          },
    [buildUrlWithSpace, contentTypeName],
  );

  const onObjectDuplicate = () => onDuplicate(objectToDuplicate);

  return (
    <PageLayout
      title={title}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        <>
          {!definedTypeName && contentTypeName !== '_tag' && (
            <TopbarButton
              label={t('ContentDefinition.Edit')}
              link={buildUrlWithSpace(
                `content-type-definitions/edit/${contentTypeName}`,
              )}
              buttonColor="blueBordered"
            />
          )}
          <TopbarButton
            label={addNew}
            link={addNewLink}
            actionDisallowed={!canCreate}
            {...(limitReachedLabel
              ? {
                  disabled: true,
                  tooltip: limitReachedLabel,
                  tooltipPlacement: 'bottomRight',
                  phoneTooltipPlacement: 'topCenter',
                }
              : {})}
            {...getTestProps(testId, `topbar-add-${contentTypeName}`, 'testId')}
          />
          <TopbarActionMenu>
            <TopbarSynchronizationButton
              onClick={() => handleExport('exportCTD', contentType?.name ?? '')}
              icon={<ExportIcon className="w-5 mb-px" />}
              label={t('SpaceSynchronization.Export.ExportButton')}
              testIdSuffix="export"
            />
            {canCreateCtd && (
              <TopbarSynchronizationButton
                onClick={() => handleImport('importCTD', contentType?.name)}
                icon={<ImportIcon className="w-5 mb-px" />}
                testIdSuffix="import"
                label={t('SpaceSynchronization.Import.ImportButton')}
              />
            )}
          </TopbarActionMenu>
        </>
      }
      secondaryTopbar={
        <DataGridControl
          displayGridCheckboxes={
            columns.length > 0 && displayLevel >= DISPLAY_STATE.LOADING_DATA
          }
          displayGridControl={
            displayLevel >= DISPLAY_STATE.EMPTY_CTD &&
            displayLevel >= DISPLAY_STATE.LOADING_DATA
          }
          editGrid={editGrid}
          filterGrid={filterGrid}
          setEditGrid={setEditGrid}
          setFilterGrid={setFilterGrid}
          handleResetGrid={handleResetGrid}
          limit={limit}
          handleLimitChange={handleLimitChange}
          columns={columns}
          gridOptions={gridOptions}
          optionsLocalStorageKey={gridLocalStorageKey}
          setGridOptions={setGridOptions}
          columnsVisibilityAdditionalClasses="bg-slate-50 w-fit"
          testId={testId}
        />
      }
      testId={testId}
      noPaddings
      {...appContextData}
    >
      <div className="w-full h-[calc(100vh-200px)] lg:h-[calc(100vh-140px)] px-5 lg:px-7">
        {gridRenderResult.length !== 0 ? (
          <ElementFromPlugin results={gridRenderResult} />
        ) : (
          <div className="flex flex-col h-full w-full">
            {hasBeforeGridPlugin && (
              <ElementFromPlugin results={beforeGridResults} />
            )}
            <div
              className={twMerge(
                predefinedLayoutClasses.dataGridContainer,
                hasBeforeGridPlugin && 'mt-2 lg:mt-2',
              )}
              ref={dataGridContainer}
            >
              {displayLevel >= DISPLAY_STATE.LOADING_DATA ? (
                <CustomizableDataGrid
                  columns={columns}
                  setColumns={setColumns}
                  data={data}
                  isLoading={displayLevel === DISPLAY_STATE.LOADING_DATA}
                  sort={sort?.sortField}
                  sortOrder={sort?.sortOrder}
                  sortingLocalStorageKey={sortingLocalStorageKey}
                  setCurrentPage={handlePageChange}
                  optionsLocalStorageKey={gridLocalStorageKey}
                  editGrid={editGrid}
                  checkedRows={checkedRows}
                  onCheckRows={handleCheckRows}
                  statusBar={
                    <StatusBar
                      rows={pagination.count}
                      checkedRows={checkedRows}
                      contentTypeName={contentTypeName}
                      handleDeleteClick={showDeleteManyModal}
                      handleMassEditClick={showMassEditModal}
                      handleMassPublishClick={handeMassPublish}
                      handleMassUnpublishClick={handeMassUnpublish}
                      handleMassArchiveClick={handeMassArchive}
                      handleDuplicateClick={
                        contentTypeName === '_tag'
                          ? undefined
                          : onObjectDuplicate
                      }
                      currentPage={page}
                      pagesCount={pagination.total_pages}
                      handlePageChange={handlePageChange}
                      handleDataUpdate={reload}
                      compactForSmall
                      resultsFrom={(pagination.current_page - 1) * limit + 1}
                      resultsTo={
                        (pagination.current_page - 1) * limit + pagination.count
                      }
                      resultsTotalCount={pagination.total_count}
                      disabled={isDeleting}
                      limitReached={limitReachedLabel}
                      isDraftPublic={contentType?.draftPublic || false}
                      {...getTestProps(testId, 'statusbar', 'testId')}
                    />
                  }
                  noDataInfoText={t('ObjectsOfType.NoMatchingData')}
                  loadingDataInfoText={
                    isDeleting
                      ? t('ObjectsOfType.DeletingCount', {
                          deletingCount: checkedRows.length || 1,
                        })
                      : ''
                  }
                  hasFilters={filterGrid}
                  disableFilters={editGrid || isDeleting}
                  filters={filters}
                  onFilter={onFilter}
                  additionalClasses={twMerge(
                    isDeleting && 'opacity-50 pointer-events-none',
                  )}
                  hasSelectColumn
                  contentType={contentType}
                  getRowLinkOptions={getRowLinkOptions}
                  {...getTestProps(testId, 'grid', 'testId')}
                />
              ) : (
                <div className={predefinedLayoutClasses.whiteBox}>
                  {emptyData}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ContentTypeObjects;
